import { useEffect, useState } from 'react';
import type { Encargo, EstadoEncargo } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Modal } from '../componentes/Modal.js';

/**
 * PORTAL DEL PROVEEDOR
 *
 * Lo que ve es MUY POCO, y lo es a propósito. Tiene una sola pantalla: sus
 * encargos. Ni propiedades, ni contratos, ni tarifas, ni clientes, ni finanzas,
 * ni informes, ni proyectos, ni usuarios.
 *
 * Y dentro de cada encargo ve lo justo para trabajar: qué hay que hacer, dónde
 * está la casa, las fotos que el equipo le ha compartido y sus instrucciones.
 * No hay notas internas, ni de quién es la casa, ni cuánto se le va a cobrar al
 * propietario, ni qué han presupuestado los demás.
 *
 * Una decisión que la pantalla hace explícita: cuando termina, el botón dice
 * «solicitar cierre», no «completar». Quien paga es quien da el trabajo por
 * bueno, y la interfaz no debe sugerir lo contrario.
 */

const TONO: Record<EstadoEncargo, string> = {
  ASIGNADO: 'ambar',
  ACEPTADO: 'neutra',
  EN_CURSO: 'verde',
  FIN_SOLICITADA: 'verde',
  COMPLETADO: 'neutra',
  RECHAZADO: 'rojo',
};

function dinero(cents: number, idioma: Idioma): string {
  return new Intl.NumberFormat(idioma === 'es' ? 'es-ES' : idioma === 'de' ? 'de-DE' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

export function PortalProveedor({ idioma, nombre }: { idioma: Idioma; nombre: string }) {
  const [encargos, setEncargos] = useState<Encargo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<null | { tipo: 'rechazar' | 'presupuesto'; id: string }>(null);

  const cargar = () => {
    api
      .misEncargos()
      .then((r) => setEncargos(r.encargos))
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      );
  };

  useEffect(cargar, [idioma]);

  const hacer = (p: Promise<unknown>) => {
    setError(null);
    void p
      .then(cargar)
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      );
  };

  if (error && encargos === null) {
    return (
      <main>
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      </main>
    );
  }
  if (encargos === null) return <p className="suave">{t(idioma, 'app.cargando')}</p>;

  const abiertos = encargos.filter((e) => e.estado !== 'COMPLETADO' && e.estado !== 'RECHAZADO');
  const cerrados = encargos.filter((e) => e.estado === 'COMPLETADO' || e.estado === 'RECHAZADO');

  return (
    <main className="portal">
      <header className="portal-cabeza">
        <h1>{t(idioma, 'prov.hola', { nombre })}</h1>
        <p className="suave">{t(idioma, 'prov.resumen', { total: abiertos.length })}</p>
      </header>

      {error && (
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      )}

      {abiertos.length === 0 && (
        <p className="suave">{t(idioma, 'prov.sinEncargos')}</p>
      )}

      {abiertos.map((e) => (
        <Ficha key={e.id} idioma={idioma} encargo={e} onAccion={hacer} onModal={setModal} />
      ))}

      {cerrados.length > 0 && (
        <details className="plegable">
          <summary>{t(idioma, 'prov.historico', { total: cerrados.length })}</summary>
          <div className="cuerpo">
            {cerrados.map((e) => (
              <Ficha key={e.id} idioma={idioma} encargo={e} onAccion={hacer} onModal={setModal} />
            ))}
          </div>
        </details>
      )}

      {modal?.tipo === 'rechazar' && (
        <ModalRechazar
          idioma={idioma}
          onEnviar={(motivo) => hacer(api.rechazarEncargo(modal.id, motivo))}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal?.tipo === 'presupuesto' && (
        <ModalPresupuesto
          idioma={idioma}
          onEnviar={(importeCents, notas) =>
            hacer(api.presupuestoEncargo(modal.id, { importeCents, notas }))
          }
          onCerrar={() => setModal(null)}
        />
      )}
    </main>
  );
}

function Ficha({
  idioma,
  encargo: e,
  onAccion,
  onModal,
}: {
  idioma: Idioma;
  encargo: Encargo;
  onAccion: (p: Promise<unknown>) => void;
  onModal: (m: { tipo: 'rechazar' | 'presupuesto'; id: string }) => void;
}) {
  const vivo = e.estado !== 'COMPLETADO' && e.estado !== 'RECHAZADO';

  return (
    <section className="tarjeta encargo">
      <div className="tarjeta-cabeza">
        <h2>{e.titulo}</h2>
        <span className={`pastilla ${TONO[e.estado]}`}>{t(idioma, `encargo.${e.estado}`)}</span>
      </div>

      <p className="codigo-inc">
        {e.codigo} · N{e.nivel}
        {e.area ? ` · ${t(idioma, `area.${e.area}`)}` : ''}
      </p>

      {/* Dónde está la casa: sin esto no puede ir a trabajar. */}
      <p>
        <b>{e.propiedadNombre}</b>
        {e.propiedadZona ? ` · ${e.propiedadZona}` : ''}
      </p>
      {e.propiedadDireccion && <p className="suave">{e.propiedadDireccion}</p>}

      {e.descripcion && <p className="prosa">{e.descripcion}</p>}

      {e.instrucciones && (
        <div className="encargo-instrucciones">
          <h4>{t(idioma, 'prov.instrucciones')}</h4>
          <p className="prosa">{e.instrucciones}</p>
        </div>
      )}

      {e.fotos.length > 0 && (
        <>
          <h4>{t(idioma, 'portal.fotos')}</h4>
          <div className="portal-fotos">
            {e.fotos.map((f) => (
              <a key={f} href={api.urlAdjunto(f)} target="_blank" rel="noreferrer">
                <img src={api.urlAdjunto(f)} alt="" />
              </a>
            ))}
          </div>
        </>
      )}

      {e.presupuestoCents !== null && (
        <p className="suave">
          {t(idioma, 'prov.tuPresupuesto')}:{' '}
          <span className="numerico">{dinero(e.presupuestoCents, idioma)}</span>
        </p>
      )}

      {e.motivoRechazo && (
        <p className="suave">
          {t(idioma, 'prov.motivoRechazo')}: {e.motivoRechazo}
        </p>
      )}

      {vivo && (
        <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
          {e.estado === 'ASIGNADO' && (
            <button type="button" onClick={() => onAccion(api.aceptarEncargo(e.id))}>
              ✓ {t(idioma, 'prov.aceptar')}
            </button>
          )}
          {e.estado === 'ACEPTADO' && (
            <button type="button" onClick={() => onAccion(api.iniciarEncargo(e.id))}>
              ▶ {t(idioma, 'prov.iniciar')}
            </button>
          )}
          {e.estado === 'EN_CURSO' && (
            <button type="button" onClick={() => onAccion(api.solicitarFinEncargo(e.id))}>
              {/* No dice «completar»: quien paga da el trabajo por bueno. */}
              {t(idioma, 'prov.solicitarFin')}
            </button>
          )}
          {e.estado === 'FIN_SOLICITADA' && (
            <span className="suave">{t(idioma, 'prov.esperandoCierre')}</span>
          )}
          <button
            type="button"
            className="secundario"
            onClick={() => onModal({ tipo: 'presupuesto', id: e.id })}
          >
            € {t(idioma, 'prov.presupuestar')}
          </button>
          {(e.estado === 'ASIGNADO' || e.estado === 'ACEPTADO') && (
            <button
              type="button"
              className="secundario"
              onClick={() => onModal({ tipo: 'rechazar', id: e.id })}
            >
              {t(idioma, 'prov.rechazar')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function ModalRechazar({
  idioma,
  onEnviar,
  onCerrar,
}: {
  idioma: Idioma;
  onEnviar: (motivo: string) => void;
  onCerrar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <Modal idioma={idioma} titulo={t(idioma, 'prov.rechazar')} onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(ev) => {
          ev.preventDefault();
          if (motivo.trim().length < 3) return;
          onEnviar(motivo.trim());
          onCerrar();
        }}
      >
        <div className="campo">
          <label htmlFor="rech-motivo">{t(idioma, 'prov.porQue')}</label>
          <textarea
            id="rech-motivo"
            rows={3}
            value={motivo}
            onChange={(ev) => setMotivo(ev.target.value)}
            required
            minLength={3}
          />
        </div>
        <p className="suave" style={{ fontSize: '0.8125rem' }}>
          {t(idioma, 'prov.porQueNota')}
        </p>
        <button type="submit" className="acceso-entrar">
          {t(idioma, 'prov.rechazar')}
        </button>
      </form>
    </Modal>
  );
}

function ModalPresupuesto({
  idioma,
  onEnviar,
  onCerrar,
}: {
  idioma: Idioma;
  onEnviar: (importeCents: number, notas?: string) => void;
  onCerrar: () => void;
}) {
  const [importe, setImporte] = useState('');
  const [notas, setNotas] = useState('');
  return (
    <Modal idioma={idioma} titulo={t(idioma, 'prov.presupuestar')} icono="euro" onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(ev) => {
          ev.preventDefault();
          const n = Number(importe.replace(',', '.'));
          if (!Number.isFinite(n) || n < 0) return;
          onEnviar(Math.round(n * 100), notas.trim() || undefined);
          onCerrar();
        }}
      >
        <div className="campo">
          <label htmlFor="pres-importe">{t(idioma, 'rapido.importe')}</label>
          <input
            id="pres-importe"
            type="text"
            inputMode="decimal"
            value={importe}
            placeholder="0,00"
            onChange={(ev) => setImporte(ev.target.value)}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="pres-notas">{t(idioma, 'prov.queIncluye')}</label>
          <textarea
            id="pres-notas"
            rows={3}
            value={notas}
            onChange={(ev) => setNotas(ev.target.value)}
          />
        </div>
        <button type="submit" className="acceso-entrar">
          {t(idioma, 'prov.enviarPresupuesto')}
        </button>
      </form>
    </Modal>
  );
}
